import React from "react";
import { Box, List, ListItemButton, ListItemIcon, ListItemText, Typography } from "@mui/material";
import DashboardIcon from "@mui/icons-material/DashboardRounded";
import ArticleIcon from "@mui/icons-material/ArticleRounded";
import AccountCircleIcon from "@mui/icons-material/AccountCircleRounded";
import PeopleIcon from "@mui/icons-material/PeopleRounded";
import SettingsIcon from "@mui/icons-material/SettingsRounded";
import TimelineIcon from "@mui/icons-material/TimelineRounded";
import InventoryIcon from "@mui/icons-material/Inventory2Rounded";
import WidgetsIcon from "@mui/icons-material/WidgetsRounded";
import ViewCarouselIcon from "@mui/icons-material/ViewCarouselRounded";
import CollectionsBookmarkRoundedIcon from "@mui/icons-material/CollectionsBookmarkRounded";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLongRounded";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchangeRounded";
import StorefrontIcon from "@mui/icons-material/StorefrontRounded";
import StarRateRoundedIcon from "@mui/icons-material/StarRateRounded";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import PaymentRoundedIcon from "@mui/icons-material/PaymentRounded";
import NotificationsRoundedIcon from "@mui/icons-material/NotificationsRounded";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import { NavLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

type Item = { to: string; label: string; icon: React.ReactNode };
type Section = { title: string; items: Item[] };

const SideBarMenus: React.FC<{ onNavigate?: () => void }> = ({ onNavigate }) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();

  const sections: Section[] = [
    {
      title: "Overview",
      items: [
        { to: "/", label: t("sidebar.dashboard"), icon: <DashboardIcon /> },
        { to: "/graphs", label: t("sidebar.graphs"), icon: <TimelineIcon /> },
      ],
    },
    {
      title: "Catalog",
      items: [
        { to: "/products", label: t("sidebar.products"), icon: <InventoryIcon /> },
        { to: "/categories", label: t("sidebar.categories"), icon: <WidgetsIcon /> },
        { to: "/collections", label: t("sidebar.collections"), icon: <CollectionsBookmarkRoundedIcon /> },
        { to: "/bulk-upload", label: "Bulk Upload", icon: <CloudUploadRoundedIcon /> },
        { to: "/slide-image", label: t("sidebar.slideImage"), icon: <ViewCarouselIcon /> },
      ],
    },
    {
      title: "Sales",
      items: [
        { to: "/orders", label: t("sidebar.orders"), icon: <ReceiptLongIcon /> },
        { to: "/refunds", label: t("sidebar.refunds"), icon: <CurrencyExchangeIcon /> },
        { to: "/payments", label: t("sidebar.payments"), icon: <PaymentRoundedIcon /> },
        { to: "/reviews", label: t("sidebar.reviews"), icon: <StarRateRoundedIcon /> },
      ],
    },
    {
      title: "Customers & Stores",
      items: [
        { to: "/shop", label: t("sidebar.shop"), icon: <StorefrontIcon /> },
        { to: "/users", label: t("sidebar.users"), icon: <PeopleIcon /> },
        { to: "/addresses", label: t("sidebar.addresses"), icon: <LocationOnRoundedIcon /> },
        { to: "/notifications", label: t("sidebar.notifications"), icon: <NotificationsRoundedIcon /> },
      ],
    },
    {
      title: "Account",
      items: [
        { to: "/profile", label: t("sidebar.profile"), icon: <AccountCircleIcon /> },
        { to: "#", label: t("sidebar.reports"), icon: <ArticleIcon /> },
        { to: "/settings", label: t("sidebar.setting"), icon: <SettingsIcon /> },
      ],
    },
  ];

  const isActive = (to: string) =>
    to === "/" ? pathname === "/" : to !== "#" && pathname.startsWith(to);

  return (
    <Box sx={{ px: 1.75, pb: 2 }}>
      {sections.map((section) => (
        <Box key={section.title} sx={{ mt: 2.25 }}>
          <Typography
            variant="caption"
            sx={{
              px: 1.5,
              display: "block",
              fontWeight: 700,
              fontSize: 11.5,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              color: "#94A3B8",
            }}
          >
            {section.title}
          </Typography>
          <List dense disablePadding sx={{ mt: 0.5 }}>
            {section.items.map((item) => {
              const active = isActive(item.to);
              return (
                <ListItemButton
                  key={item.label}
                  component={NavLink}
                  to={item.to}
                  onClick={onNavigate}
                  selected={active}
                  sx={{
                    position: "relative",
                    borderRadius: "10px",
                    mb: 0.25,
                    py: 0.95,
                    color: "#475569",
                    transition: "background-color .15s ease, color .15s ease",
                    "& .MuiListItemIcon-root": { minWidth: 38, color: active ? "primary.main" : "#94A3B8" },
                    "&:hover": { bgcolor: "#F1F4F9", color: "#0F172A", "& .MuiListItemIcon-root": { color: "primary.main" } },
                    "&.Mui-selected": {
                      bgcolor: "primary.light",
                      color: "primary.main",
                      "&:hover": { bgcolor: "primary.light" },
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        left: -12,
                        top: 8,
                        bottom: 8,
                        width: 4,
                        borderRadius: "0 4px 4px 0",
                        bgcolor: "secondary.main",
                      },
                    },
                  }}
                >
                  <ListItemIcon>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.label}
                    primaryTypographyProps={{ fontSize: 14, fontWeight: active ? 700 : 500 }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        </Box>
      ))}
    </Box>
  );
};

export default SideBarMenus;
